import { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { CompanyData, CFOPOption, UFOption, TaxRegimeOption, ValidationError } from '../types';

interface CompanyDataFormProps {
  data: CompanyData;
  onChange: (data: CompanyData) => void;
  errors: ValidationError[];
}

const CompanyDataForm = ({ data, onChange, errors }: CompanyDataFormProps) => {
  const [cnpjDisplay, setCnpjDisplay] = useState(data.cnpj);

  const ufOptions: UFOption[] = [
    { value: 'AC', label: 'AC - Acre' },
    { value: 'AL', label: 'AL - Alagoas' },
    { value: 'AP', label: 'AP - Amapá' },
    { value: 'AM', label: 'AM - Amazonas' },
    { value: 'BA', label: 'BA - Bahia' },
    { value: 'CE', label: 'CE - Ceará' },
    { value: 'DF', label: 'DF - Distrito Federal' },
    { value: 'ES', label: 'ES - Espírito Santo' },
    { value: 'GO', label: 'GO - Goiás' },
    { value: 'MA', label: 'MA - Maranhão' },
    { value: 'MT', label: 'MT - Mato Grosso' },
    { value: 'MS', label: 'MS - Mato Grosso do Sul' },
    { value: 'MG', label: 'MG - Minas Gerais' },
    { value: 'PA', label: 'PA - Pará' },
    { value: 'PB', label: 'PB - Paraíba' },
    { value: 'PR', label: 'PR - Paraná' },
    { value: 'PE', label: 'PE - Pernambuco' },
    { value: 'PI', label: 'PI - Piauí' },
    { value: 'RJ', label: 'RJ - Rio de Janeiro' },
    { value: 'RN', label: 'RN - Rio Grande do Norte' },
    { value: 'RS', label: 'RS - Rio Grande do Sul' },
    { value: 'RO', label: 'RO - Rondônia' },
    { value: 'RR', label: 'RR - Roraima' },
    { value: 'SC', label: 'SC - Santa Catarina' },
    { value: 'SP', label: 'SP - São Paulo' },
    { value: 'SE', label: 'SE - Sergipe' },
    { value: 'TO', label: 'TO - Tocantins' }
  ];

  const cfopOptions: CFOPOption[] = [
    { value: '5101', label: '5101', description: 'Venda de produção do estabelecimento' },
    { value: '5102', label: '5102', description: 'Venda de mercadoria adquirida ou recebida de terceiros' },
    { value: '5103', label: '5103', description: 'Venda de produção do estabelecimento, efetuada fora do estabelecimento' },
    { value: '5104', label: '5104', description: 'Venda de mercadoria adquirida ou recebida de terceiros, efetuada fora do estabelecimento' },
    { value: '5105', label: '5105', description: 'Venda de produção do estabelecimento que não deva por ele transitar' },
    { value: '6101', label: '6101', description: 'Venda de produção do estabelecimento - Operação interestadual' },
    { value: '6102', label: '6102', description: 'Venda de mercadoria adquirida ou recebida de terceiros - Operação interestadual' },
    { value: '6103', label: '6103', description: 'Venda de produção do estabelecimento, efetuada fora do estabelecimento - Interestadual' },
    { value: '6104', label: '6104', description: 'Venda de mercadoria adquirida ou recebida de terceiros, efetuada fora do estabelecimento - Interestadual' }
  ];

  const taxRegimeOptions: TaxRegimeOption[] = [
    { value: 'simples_nacional', label: 'Simples Nacional' },
    { value: 'lucro_presumido', label: 'Lucro Presumido' },
    { value: 'lucro_real', label: 'Lucro Real' }
  ];

  const formatCNPJ = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const validateCNPJ = (cnpj: string): boolean => {
    const numbers = cnpj.replace(/\D/g, '');
    return numbers.length === 14;
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    setCnpjDisplay(formatted);
    const numbers = formatted.replace(/\D/g, '');
    onChange({ ...data, cnpj: numbers });
  };

  useEffect(() => {
    setCnpjDisplay(formatCNPJ(data.cnpj));
  }, [data.cnpj]);

  const getError = (field: string): string | undefined => {
    return errors.find(e => e.field === field)?.message;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Dados da Empresa</h3>
        
        <div className="space-y-4">
          <Input
            label="CNPJ"
            type="text"
            placeholder="00.000.000/0000-00"
            value={cnpjDisplay}
            onChange={handleCNPJChange}
            error={getError('cnpj')}
            required
            maxLength={18}
            description="Formato: 00.000.000/0000-00"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="UF Origem"
              placeholder="Selecione o estado"
              options={ufOptions}
              value={data.ufOrigin}
              onChange={(value) => onChange({ ...data, ufOrigin: value as string })}
              error={getError('ufOrigin')}
              required
              searchable
            />

            <Select
              label="UF Destino"
              placeholder="Selecione o estado"
              options={ufOptions}
              value={data.ufDestination}
              onChange={(value) => onChange({ ...data, ufDestination: value as string })}
              error={getError('ufDestination')}
              required
              searchable
            />
          </div>

          <Select
            label="CFOP"
            placeholder="Selecione o código"
            options={cfopOptions}
            value={data.cfop}
            onChange={(value) => onChange({ ...data, cfop: value as string })}
            error={getError('cfop')}
            required
            searchable
            description={cfopOptions.find(opt => opt.value === data.cfop)?.description}
          />

          <Input
            label="Características Tributárias"
            type="text"
            placeholder="Descreva as características"
            value={data.taxCharacteristics}
            onChange={(e) => onChange({ ...data, taxCharacteristics: e.target.value })}
            error={getError('taxCharacteristics')}
          />

          <Input
            label="Finalidade"
            type="text"
            placeholder="Finalidade da operação"
            value={data.purpose}
            onChange={(e) => onChange({ ...data, purpose: e.target.value })}
            error={getError('purpose')}
            required
          />

          <Select
            label="Regime Tributário"
            placeholder="Selecione o regime"
            options={taxRegimeOptions}
            value={data.taxRegime}
            onChange={(value) => onChange({ ...data, taxRegime: value as string })}
            error={getError('taxRegime')}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyDataForm;